import { ButtonType, ButtonWidget, InteractionState } from '@tableau/widgets';
import React from 'react';

import { CommonStyles } from '~common/react/CommonStyles';
import { imgPath } from '~common/utilities/ImgUrl';
import { PageHeaderStyles } from '~src/spacesPlatform/components/layout/PageHeaderStyles';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { HeaderSearchBox } from '../header/HeaderSearchBox';
import { PageHeaderButtonType, PageHeaderIconPaths, PageHeaderIcons } from './PageHeader';
import cssVar = PageHeaderStyles.CssVariables.cssVar;
import CssVariables = PageHeaderStyles.CssVariables;

export const vizHeaderSearchButtonImgTestId = 'viz-header-search-button-img';

export interface SearchProps {
  headerSearchStyle: string;
  searchClosed: (event: BaseJQueryEventObject) => void;
  searchClosedWithoutNeedingFocus: (event: BaseJQueryEventObject) => void;
  letSearchBoxHasFocus?: boolean;
  isSearchInputBoxActive: (isActive: boolean) => void;
}

export function PageHeaderSearch(props: SearchProps): JSX.Element {
  return (
    <div className={props.headerSearchStyle}>
      <HeaderSearchBox
        omniboxOpenWidth={350}
        searchClosed={props.searchClosed}
        searchClosedWithoutNeedingFocus={props.searchClosedWithoutNeedingFocus}
        isSearchOpen={true}
        letSearchBoxHasFocus={props.letSearchBoxHasFocus}
        isSearchInputBoxActive= {props.isSearchInputBoxActive}
      />
    </div>
  );
}

const glassIcons: PageHeaderIcons = {
  lightIcons: {
    active: imgPath('spaces_toolbar_search_active_white.svg'),
    inactive: imgPath('spaces_toolbar_search_white.svg')
  },
  mediumIcons: { // Same as largeIcons for now until we update search to a shared widget
    active: imgPath('spaces_toolbar_search_active.svg'),
    inactive: imgPath('spaces_toolbar_search.svg')
  },
  largeIcons: {
    active: imgPath('spaces_toolbar_search_active.svg'),
    inactive: imgPath('spaces_toolbar_search.svg')
  }
};

const closeIcons: PageHeaderIcons = {
  lightIcons: {
    active: imgPath('spaces_toolbar_delete_active_white.svg'),
    inactive: imgPath('spaces_toolbar_delete_white.svg')
  },
  mediumIcons: { // Same as largeIcons for now until we update search to a shared widget
    active: imgPath('spaces_toolbar_delete_active.svg'),
    inactive: imgPath('spaces_toolbar_delete.svg')
  },
  largeIcons: {  // this should never need non-white icons
    active: imgPath('spaces_toolbar_delete_active.svg'),
    inactive: imgPath('spaces_toolbar_delete.svg')
  }
};

interface IconInfo {
  src: string;
  width: number;
  height: number;
}

function buttonIconInfo(isSearchOpen: boolean, showActiveIcon: boolean, buttonType: PageHeaderButtonType): IconInfo {
  const iconSource = (isSearchOpen ? closeIcons : glassIcons);
  const newHeader = (buttonType === 'short' && FeatureFlags.vizToolbarRedesignEnabled) ? iconSource.mediumIcons : iconSource.largeIcons;
  const icons: PageHeaderIconPaths = (buttonType === 'light') ? iconSource.lightIcons : newHeader;
  return {
    width: 32,
    height: 32,
    src: showActiveIcon ? icons.active : icons.inactive
  };
}

const getButtonContent = (isSearchOpen: boolean, buttonType: PageHeaderButtonType): (state: InteractionState) => JSX.Element => {
  return (state: InteractionState): JSX.Element => {
    const showActiveIcon = state.hover || state.active || state.focus;
    const iconInfo = buttonIconInfo(isSearchOpen, showActiveIcon, buttonType);
    let iconStyle: React.CSSProperties = {width: iconInfo.width, height: iconInfo.height, marginRight: '0px'};
    const wrappingDivHoverStyle = FeatureFlags.vizToolbarRedesignEnabled
      ? {
        backgroundColor: state.hover ? cssVar(CssVariables.headerBackgroundHoverColor) : 'transparent'
      }
      : null;

     // Keep alt text blank since button has a title: TFS 1036309
    return (
      <div style={{...CommonStyles.Layout.FlexBlockLeftAndCenter, ...wrappingDivHoverStyle}}>
        <img
          style={iconStyle}
          src={iconInfo.src}
          data-tb-test-id={vizHeaderSearchButtonImgTestId}
          alt=""
        />
      </div>
    );
  };
};

export interface VizViewerSearchButtonProps {
  readonly isSearchOpen: boolean;
  readonly title: string;
  readonly testId: string;
  readonly onClick: () => void;
  readonly setRef: (button: ButtonWidget) => void;
}

export function VizViewerSearchButton(props: VizViewerSearchButtonProps): JSX.Element {
  return (
    <div style={{lineHeight: 0, marginRight: '-2px'}}>
      <ButtonWidget
        buttonType={ButtonType.IconOutline}
        getContent={getButtonContent(props.isSearchOpen, FeatureFlags.vizToolbarRedesignEnabled ? 'regular' : 'light')}
        title={props.title}
        testId={props.testId}
        handleClick={props.onClick}
        ref={props.setRef}
        style={{cursor: 'pointer'}}
      />
    </div>
  );
}
